import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AnalysisCard.css';
import Modal from './Modal';
import Icon from '../design-system/primitives/Icon';
import { apiService } from '../services/api';
import { useToast } from '../context/ToastContext';

const AnalysisCard = ({ analysis, isNewCard = false, onAnalysisDeleted }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const handleClick = async () => {
    if (isNewCard) {
      setIsCreating(true);
      try {
        const resp = await apiService.createAnalysis({ name: 'Untitled Analysis', description: '' });
        navigate(`/app/analysis/${resp.data.id}`);
      } catch (err) {
        if (err.response?.status === 403) {
          const msg = err.response?.data?.detail || 'You have reached your analysis limit.';
          toast(msg, 'error');
        }
      } finally {
        setIsCreating(false);
      }
    } else {
      navigate(`/app/analysis/${analysis.id}`);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await apiService.deleteAnalysis(analysis.id);
      setShowDeleteModal(false);
      if (onAnalysisDeleted) onAnalysisDeleted(analysis.id);
    } catch (error) {
      console.error('Failed to delete analysis:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => setShowDeleteModal(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  if (isNewCard) {
    return (
      <div
        className={`analysis-card analysis-card--new${isCreating ? ' analysis-card--loading' : ''}`}
        onClick={isCreating ? undefined : handleClick}
        style={isCreating ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
      >
        <div className="analysis-card__content analysis-card__content--new">
          <div className="analysis-card__icon">
            {isCreating ? (
              <div className="loading-spinner" />
            ) : (
              <Icon name="plus" size={20} />
            )}
          </div>
          <h3 className="analysis-card__title">New analysis</h3>
          <p className="analysis-card__subtitle">
            {isCreating ? 'Creating…' : 'Start a new conversation'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="analysis-card" onClick={handleClick}>
        {analysis.viewer_role === 'owner' && (
          <button
            className="analysis-card__delete-btn"
            onClick={handleDeleteClick}
            aria-label="Delete analysis"
            title="Delete analysis"
          >
            <Icon name="trash-2" size={14} />
          </button>
        )}

        <div className="analysis-card__content">
          <div className="analysis-card__header">
            <h3 className="analysis-card__title">{analysis.name}</h3>
            <div className="analysis-card__meta">
              <span className="analysis-card__date">
                Edited {formatDate(analysis.last_edited_at)}
              </span>
              <div className="analysis-card__artifact_counts">
                {analysis.is_demo && (
                  <span className="analysis-card__artifact_counts__item analysis-card__artifact_demo__item">
                    Demo
                  </span>
                )}
                {analysis.viewer_role && analysis.viewer_role !== 'owner' && (
                  <span className="analysis-card__artifact_counts__item analysis-card__artifact_shared__item">
                    <Icon name="users" size={11} />
                    Shared
                  </span>
                )}
                {analysis.datasets_count > 0 && (
                  <span className="analysis-card__artifact_counts__item">
                    <Icon name="database" size={11} />
                    {analysis.datasets_count} Dataset{analysis.datasets_count !== 1 ? 's' : ''}
                  </span>
                )}
                {analysis.models_count > 0 && (
                  <span className="analysis-card__artifact_counts__item">
                    <Icon name="brain" size={11} />
                    {analysis.models_count} Model{analysis.models_count !== 1 ? 's' : ''}
                  </span>
                )}
                {analysis.is_priceless && (
                  <span className="analysis-card__artifact_counts__item">
                    <Icon name="sparkles" size={11} />
                    Priceless Insights
                  </span>
                )}
              </div>
            </div>
          </div>

          {analysis.description && (
            <p className="analysis-card__description">{analysis.description}</p>
          )}

          <div className="analysis-card__footer">
            <span className="analysis-card__created">
              Created {formatDate(analysis.created_at)}
            </span>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Danger Zone"
        message={`Are you sure you want to delete "${analysis.name}"? This action will permanently delete the analysis and all its associated datasets and files. This cannot be undone.`}
        confirmText="Delete Analysis"
        cancelText="Cancel"
        isDangerous={true}
        isLoading={isDeleting}
        informationLevel="error"
      />
    </>
  );
};

export default AnalysisCard;
